import type { AccreditationSummary } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { Link } from "@tanstack/react-router";
import type { ComponentType, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import { CardIcon, CheckIcon, InboxIcon, WarnIcon } from "./icons";
import { type DashboardSearch, type RecordsSearch, pct, recordsSearch, statusCounts } from "./model";

/**
 * Her four KPI tiles (`.kt`): icon tile, small uppercase label, big number,
 * one muted line — every number from the ONE summary response. Each tile
 * opens View Records with the dashboard's filters and the tile's statuses.
 */
export function KpiTiles({ summary, search }: { summary: AccreditationSummary | undefined; search: DashboardSearch }) {
	const { t } = useTranslation("dashboard");
	const { formatNumber: fmt } = useUiStrings();
	const by = statusCounts(summary);
	const total = summary?.total ?? 0;
	const review = by.SUBMITTED + by.PENDING;
	const back = by.RETURN + by.REJECTED;

	return (
		<div className="db-kt">
			<Tile
				tone="n"
				icon={CardIcon}
				label={t("kpi.total")}
				n={summary && fmt(total)}
				sub={summary && t("kpi.totalSub", { count: summary.by_organization.length })}
				to={recordsSearch(search)}
			/>
			<Tile
				tone="ok"
				icon={CheckIcon}
				label={t("kpi.approved")}
				n={summary && fmt(by.APPROVED)}
				sub={summary && t("kpi.approvedSub", { percent: fmt(pct(by.APPROVED, total)) })}
				to={recordsSearch(search, { status: "APPROVED" })}
			/>
			<Tile
				tone="bl"
				icon={InboxIcon}
				label={t("kpi.review")}
				n={summary && fmt(review)}
				sub={summary && t("kpi.reviewSub", { submitted: fmt(by.SUBMITTED), pending: fmt(by.PENDING) })}
				to={recordsSearch(search, { status: "SUBMITTED,PENDING" })}
			/>
			<Tile
				tone="r"
				icon={WarnIcon}
				label={t("kpi.back")}
				n={summary && fmt(back)}
				sub={summary && t("kpi.backSub", { returned: fmt(by.RETURN), rejected: fmt(by.REJECTED) })}
				to={recordsSearch(search, { status: "RETURN,REJECTED" })}
			/>
		</div>
	);
}

function Tile({
	tone,
	icon: Icon,
	label,
	n,
	sub,
	to,
}: {
	tone: "n" | "ok" | "bl" | "r";
	icon: ComponentType<SVGProps<SVGSVGElement>>;
	label: string;
	/** undefined while loading: skeletons in the tile's shape, never "—". */
	n: string | undefined;
	sub: string | undefined;
	to: RecordsSearch;
}) {
	return (
		<Link to="/accreditation/records" search={to} className={`db-kt__i db-kt--${tone}`}>
			<span className="db-kt__ic">
				<Icon />
			</span>
			<span className="db-kt__t">
				<span className="db-kt__l">{label}</span>
				{n === undefined ? <span className="skeleton my-[0.25rem] block h-[1.5rem] w-[4.5rem]" /> : <b className="db-kt__n">{n}</b>}
				{sub === undefined ? <span className="skeleton block h-[0.75rem] w-[7rem]" /> : <small className="db-kt__s">{sub}</small>}
			</span>
		</Link>
	);
}
